import { useEffect, useState } from 'react';
import type { Priority } from '../../common/ui/types';
import { parsePriorityOnServerToPriority } from '../../common/utils';
import { fetchPrioritiesByScheme } from '../../services/priorities';
import { emptyFilter, fetchPrioritySchemes } from '../../services/priority-schemes-page/main';

export type usePrioritySchemeMappingsOutParams = {
	requiresRemovedProjectsMapping: boolean;
};

export type PrioritySchemeMappingsOutReturn = {
	defaultSchemeId: string | undefined;
	defaultSchemePriorities: Priority[];
	isDefaultSchemeLoading: boolean;
	isDefaultSchemeError: boolean;
};

export const usePrioritySchemeMappingsOut = ({
	requiresRemovedProjectsMapping,
}: usePrioritySchemeMappingsOutParams): PrioritySchemeMappingsOutReturn => {
	const [defaultSchemeId, setDefaultSchemeId] = useState<string | undefined>(undefined);
	const [defaultSchemePriorities, setDefaultSchemePriorities] = useState<Array<Priority>>([]);
	const [isDefaultSchemeLoading, setIsDefaultSchemeLoading] = useState<boolean>(false);
	const [isDefaultSchemeError, setIsDefaultSchemeError] = useState<boolean>(false);

	useEffect(() => {
		(async () => {
			// To ensure that we only fetch one time -> when we first need to do remove project
			// mapping and when we haven't set the default scheme before.
			if (!requiresRemovedProjectsMapping || defaultSchemeId) return;
			try {
				setIsDefaultSchemeLoading(true);
				setIsDefaultSchemeError(false);
				const response = await fetchPrioritySchemes(1, emptyFilter, true);

				if (response.prioritySchemes.length === 0) {
					throw Error('Empty priority scheme when fetching default scheme');
				}

				const responseSchemeId = response.prioritySchemes[0].id;
				setDefaultSchemeId(responseSchemeId);

				let startAt = 0;
				let isLast = false;
				let defaultPriorities: Priority[] = [];

				/**
				 * In the interest of time we will preload default scheme priorities instead of paginating it properly
				 * it from the paginated API, we will cap the amount of priorities we can fetch at 2000, we have a
				 * soft limit of 300 priorities in an instance however customers in server may migrate over with
				 * a ridiculous amount of priorities (in server as of now the max is only 200, but that may change).
				 *
				 * The current expected behaviour is that only 2000 priorities will be avaliable when mapping to the
				 * default scheme - this is already more than the 300 priorities we have communicated that we are
				 * supporting.
				 *
				 * When this edge case occurs, we will update our code to handle this edge case, by updating the
				 * mapping picker component to handle both dealing with both in memory priorities and fetching from an
				 * API.
				 */
				while (!isLast && startAt < 2000) {
					try {
						// eslint-disable-next-line no-await-in-loop
						const defaultPriorityPaginated = await fetchPrioritiesByScheme(
							responseSchemeId,
							0,
							1000,
						);

						startAt += defaultPriorityPaginated.maxResults;
						isLast = defaultPriorityPaginated.isLast;

						defaultPriorities = [
							...defaultPriorities,
							...defaultPriorityPaginated.values.map(parsePriorityOnServerToPriority),
						];
					} catch (error) {
						throw Error('Error fetching default scheme priorities');
					}
				}

				setDefaultSchemePriorities(defaultPriorities);
			} catch (error) {
				setIsDefaultSchemeError(true);
			} finally {
				setIsDefaultSchemeLoading(false);
			}
		})();
	}, [defaultSchemeId, requiresRemovedProjectsMapping, setIsDefaultSchemeLoading]);

	return {
		defaultSchemeId,
		defaultSchemePriorities,
		isDefaultSchemeLoading,
		isDefaultSchemeError,
	};
};
